import type { Dispatch, SetStateAction } from "react";
import { createPortal } from "react-dom";
import type { TFunction } from "i18next";
import {
  type CandidateInfo,
  type VoteHistoryItem,
} from "../../client";
import UserAvatar from "../../components/UserAvatar";
import ModalFrame from "../../ui/primitives/ModalFrame";
import { formatUserName } from "../../utils/format/formatUserName";
import { SHELL_ICON, STAR_ICON } from "./constants";


interface VoteHistoryModalProps {
  formatDate: (dateString: string | null | undefined) => string;
  selectedCandidateForHistory: CandidateInfo | null;
  setSelectedCandidateForHistory: Dispatch<SetStateAction<CandidateInfo | null>>;
  t: TFunction;
}

function VoteHistoryModal({
  formatDate,
  selectedCandidateForHistory,
  setSelectedCandidateForHistory,
  t,
}: VoteHistoryModalProps) {
  // Portal + z above MapStaticObjectModal City Hall (10050), same as metric charts.
  return createPortal(
    <ModalFrame
      isOpen={selectedCandidateForHistory !== null}
      onClose={() => setSelectedCandidateForHistory(null)}
      variant="dialog"
      showBackButton={true}
      title={selectedCandidateForHistory ? `${t("Voting history")} - ${(() => { const n = formatUserName(selectedCandidateForHistory).displayName; return n.length > 15 ? n.substring(0, 15) + "..." : n; })()}` : ""}
      backdropClassName="z-[10060]"
    >
      {selectedCandidateForHistory && selectedCandidateForHistory.vote_history && selectedCandidateForHistory.vote_history.length > 0 ? (
        <div
          className="flex flex-col gap-xl"
        >
          {selectedCandidateForHistory.vote_history.map((entry: VoteHistoryItem, index: number) => (
            <div
              key={index}
              className="flex items-start gap-lg rounded-md border border-white-alpha-10 bg-white-alpha-5 p-lg"
            >
              <UserAvatar
                user={{
                  photo_url: entry.voter.photo_url,
                  first_name: entry.voter.first_name,
                  last_name: entry.voter.last_name,
                  username: entry.voter.username,
                  vip_until: entry.voter.vip_until,
                }}
                size={48}
                alt={entry.voter.first_name || "Unknown"}
              />
              <div
                className="flex min-w-0 flex-1 flex-col gap-xs"
              >
                <div
                  className="[font-family:Lato,sans-serif] text-[16px] leading-[20px] font-bold text-white"
                >
                  {entry.voter.first_name && entry.voter.last_name
                    ? `${entry.voter.first_name} ${entry.voter.last_name}`
                    : entry.voter.first_name || "Unknown"}
                  {entry.voter.username && (
                    <span className="font-normal text-text-quieter">
                      {" "}
                      {entry.voter.username.startsWith("@") ? entry.voter.username : `@${entry.voter.username}`}
                    </span>
                  )}
                </div>
                <div
                  className="[font-family:Lato,sans-serif] text-[14px] leading-[18px] font-normal text-text-subtle"
                >
                  {formatDate(entry.created_at)}
                </div>
                <div
                  className="mt-xs flex items-center gap-md"
                >
                  <span
                    className="[font-family:Lato,sans-serif] text-[14px] font-semibold text-white"
                  >
                    {t("Votes")}: {entry.votes_count}
                  </span>
                  <span
                    className="[font-family:Lato,sans-serif] text-[14px] font-normal text-text-subtle"
                  >
                    •
                  </span>
                  <div
                    className="flex items-center gap-xs"
                  >
                    <img
                      src={entry.currency_type === "stars" ? STAR_ICON : SHELL_ICON}
                      alt={entry.currency_type}
                      className="h-xl object-contain"
                      style={{
                        width: entry.currency_type === "stars" ? "16px" : "20px",
                      }}
                    />
                    <span
                      className="[font-family:Lato,sans-serif] text-[14px] font-normal text-text-subtle"
                    >
                      {entry.payment_amount.toLocaleString("en-US")}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div
          className="p-3xl text-center [font-family:Lato,sans-serif] text-[16px] leading-[24px] font-normal text-text-subtle"
        >
          {t("No voting history available")}
        </div>
      )}
    </ModalFrame>,
    document.body,
  );
}

export default VoteHistoryModal;
